/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, Mail, Phone, UserPlus, Smartphone, Globe, Archive, X as XIcon } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { clientDisplayName } from "@/lib/utils";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import PracticeManagerLink from "@/components/PracticeManagerLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export default function FirmClients() {
    const { tenant } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [installerIds, setInstallerIds] = useState(new Set());
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [scope, setScope] = useState("active"); // active | archived
    const [selected, setSelected] = useState(new Set());
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [groups, setGroups] = useState([]);
    const [ungroupedCount, setUngroupedCount] = useState(0);
    const [groupFilter, setGroupFilter] = useState("all"); // all | none | <group_id>
    const [manageOpen, setManageOpen] = useState(false);

    async function loadGroups() {
        try {
            const { data } = await api.get("/client-groups");
            setGroups(data.rows || []);
            setUngroupedCount(data.ungrouped_count || 0);
        } catch { /* ignore */ }
    }

    async function load() {
        setLoading(true);
        try {
            const [{ data }, pwaResp] = await Promise.all([
                api.get("/clients/", { params: { include_archived: scope === "archived" } }),
                api.get("/portal/pwa/summary").catch(() => ({ data: null })),
            ]);
            setClients(data);
            setInstallerIds(new Set(pwaResp.data?.installer_client_ids || []));
            setSelected(new Set());
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); loadGroups(); /* eslint-disable-next-line */ }, [scope]);

    const groupsById = groups.reduce((acc, g) => { acc[g.id] = g; return acc; }, {});

    const filtered = clients
        .filter((c) => scope === "archived" ? !!c.archived_at : !c.archived_at)
        .filter((c) => {
            if (groupFilter === "all") return true;
            if (groupFilter === "none") return !c.group_id;
            return c.group_id === groupFilter;
        })
        .filter((c) => {
            const needle = q.toLowerCase();
            return (
                (c.name || "").toLowerCase().includes(needle) ||
                (c.alias || "").toLowerCase().includes(needle) ||
                (c.nickname || "").toLowerCase().includes(needle)
            );
        });

    function toggleOne(cid) {
        setSelected((s) => {
            const n = new Set(s);
            if (n.has(cid)) n.delete(cid); else n.add(cid);
            return n;
        });
    }
    function selectAllVisible() {
        setSelected(new Set(filtered.map((c) => c.id)));
    }
    function clearSelection() { setSelected(new Set()); }

    async function confirmBulkArchive() {
        setArchiving(true);
        try {
            const { data } = await api.post("/clients/bulk-archive", { client_ids: Array.from(selected) });
            toast.success(`Archived ${data.archived_count} ${data.archived_count === 1 ? "client" : "clients"} — kept every historical record.`);
            setArchiveOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setArchiving(false);
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow="Clients"
                title="Your client book"
                description="Every business you serve. Click in to see entities, document submissions, requests and timeline."
                actions={<NewClientDialog onCreated={load} />}
                testId="clients-header"
            />

            <div className="co-card co-card-pad flex items-center gap-3 flex-wrap">
                <Input
                    placeholder="Search clients…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="max-w-sm"
                    data-testid="clients-search"
                />
                {scope === "active" && filtered.length > 0 && (
                    <button
                        type="button"
                        onClick={selected.size === filtered.length ? clearSelection : selectAllVisible}
                        className="text-xs text-[#5E636E] hover:text-[#0F1115] underline underline-offset-2"
                        data-testid="clients-select-all"
                    >
                        {selected.size === filtered.length ? "Clear selection" : `Select all ${filtered.length}`}
                    </button>
                )}
                <div className="flex gap-1.5 ml-auto">
                    <button
                        type="button"
                        onClick={() => setScope("active")}
                        className={`rounded-full border px-3 py-1.5 text-xs ${scope === "active" ? "bg-[#171717] text-white border-[#171717]" : "border-[#E4E4E7] text-[#5E636E] hover:bg-zinc-50"}`}
                        data-testid="clients-scope-active"
                    >
                        Active
                    </button>
                    <button
                        type="button"
                        onClick={() => setScope("archived")}
                        className={`rounded-full border px-3 py-1.5 text-xs ${scope === "archived" ? "bg-[#171717] text-white border-[#171717]" : "border-[#E4E4E7] text-[#5E636E] hover:bg-zinc-50"}`}
                        data-testid="clients-scope-archived"
                    >
                        Archived
                    </button>
                </div>
            </div>

            <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1" data-testid="clients-group-chips">
                <button
                    type="button"
                    onClick={() => setGroupFilter("all")}
                    className={`shrink-0 rounded-full border px-3 py-1 text-xs whitespace-nowrap ${groupFilter === "all" ? "bg-[#0F1115] text-white border-[#0F1115]" : "border-[#E4E4E7] text-[#5E636E] hover:bg-zinc-50"}`}
                    data-testid="clients-group-chip-all"
                >
                    All <span className="opacity-70">({clients.length})</span>
                </button>
                {groups.map((g) => (
                    <button
                        key={g.id}
                        type="button"
                        onClick={() => setGroupFilter(g.id)}
                        className={`shrink-0 rounded-full border px-3 py-1 text-xs whitespace-nowrap inline-flex items-center gap-1.5 ${groupFilter === g.id ? "bg-[#0F1115] text-white border-[#0F1115]" : "border-[#E4E4E7] text-[#5E636E] hover:bg-zinc-50"}`}
                        data-testid={`clients-group-chip-${g.id}`}
                    >
                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: g.color }} />
                        {g.name} <span className="opacity-70">({g.client_count})</span>
                    </button>
                ))}
                {ungroupedCount > 0 && (
                    <button
                        type="button"
                        onClick={() => setGroupFilter("none")}
                        className={`shrink-0 rounded-full border px-3 py-1 text-xs whitespace-nowrap ${groupFilter === "none" ? "bg-[#0F1115] text-white border-[#0F1115]" : "border-[#E4E4E7] text-[#5E636E] hover:bg-zinc-50"}`}
                        data-testid="clients-group-chip-none"
                    >
                        Ungrouped <span className="opacity-70">({ungroupedCount})</span>
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setManageOpen(true)}
                    className="shrink-0 rounded-full border border-dashed border-[#E4E4E7] px-3 py-1 text-xs text-[#5E636E] hover:bg-zinc-50 whitespace-nowrap"
                    data-testid="clients-manage-groups"
                >
                    Manage groups
                </button>
            </div>

            {selected.size > 0 && (
                <div
                    className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3 flex-wrap"
                    data-testid="clients-selection-bar"
                >
                    <span className="text-sm font-medium text-amber-900">
                        {selected.size} {selected.size === 1 ? "client" : "clients"} selected
                    </span>
                    <Button
                        size="sm"
                        onClick={() => setArchiveOpen(true)}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="clients-bulk-archive-btn"
                    >
                        <Archive className="h-3.5 w-3.5" /> Archive selected
                    </Button>
                    <Select
                        onValueChange={async (val) => {
                            const ids = Array.from(selected);
                            if (!ids.length) return;
                            try {
                                const gid = val === "none" ? "none" : val;
                                await api.post(`/client-groups/${gid}/assign`, { client_ids: ids });
                                toast.success(`Updated ${ids.length} client${ids.length === 1 ? "" : "s"}`);
                                setSelected(new Set());
                                await Promise.all([load(), loadGroups()]);
                            } catch (e) { toast.error(formatApiError(e)); }
                        }}
                    >
                        <SelectTrigger className="h-8 text-xs bg-white" data-testid="clients-bulk-group">
                            <SelectValue placeholder="Assign to group…" />
                        </SelectTrigger>
                        <SelectContent>
                            {groups.map((g) => (
                                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                            ))}
                            <SelectItem value="none">Clear group</SelectItem>
                        </SelectContent>
                    </Select>
                    <button
                        type="button"
                        onClick={clearSelection}
                        className="ml-auto inline-flex items-center gap-1 text-xs text-amber-800 hover:text-amber-950"
                        data-testid="clients-clear-selection"
                    >
                        <XIcon className="h-3 w-3" /> Clear
                    </button>
                </div>
            )}

            <div className="mt-6">
                {loading ? (
                    <div className="text-sm text-[#5E636E]">Loading clients…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        title="No clients yet"
                        description="Add your first client to start collecting documents and conversations."
                        testId="clients-empty"
                    />
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {filtered.map((c) => {
                            const isChecked = selected.has(c.id);
                            return (
                            <div
                                role="link"
                                tabIndex={0}
                                onClick={(e) => {
                                    // If a checkbox click bubbles up, don't navigate
                                    if (e.target.closest("[data-checkbox-hit]")) return;
                                    navigate(`/app/clients/${c.id}`);
                                }}
                                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/app/clients/${c.id}`); }}
                                key={c.id}
                                className={
                                    "co-card co-card-pad hover:shadow-sm transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#171717]/30 relative " +
                                    (isChecked ? "ring-2 ring-amber-400 border-amber-300" : "")
                                }
                                data-testid={`client-card-${c.id}`}
                            >
                                {scope === "active" && (
                                    <label
                                        data-checkbox-hit
                                        onClick={(e) => e.stopPropagation()}
                                        className="absolute top-3 left-3 inline-flex h-5 w-5 items-center justify-center rounded border border-[#E4E4E7] bg-white hover:border-[#171717] cursor-pointer"
                                        title="Select for bulk actions"
                                        data-testid={`client-select-${c.id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleOne(c.id)}
                                            className="h-3.5 w-3.5 accent-amber-600"
                                        />
                                    </label>
                                )}
                                <div className={`flex items-start justify-between ${scope === "active" ? "pl-7" : ""}`}>
                                    <div>
                                        <div className="font-display text-lg font-medium" data-testid={`client-name-${c.id}`}>
                                            {clientDisplayName(c)}
                                            {c.nickname && (
                                                <span className="ml-2 text-sm text-[#5E636E] font-normal" data-testid={`client-nickname-${c.id}`}>
                                                    · {c.nickname}
                                                </span>
                                            )}
                                        </div>
                                        {c.alias && (
                                            <div className="text-[11px] text-[#9CA0A8]" data-testid={`client-legal-name-${c.id}`}>Legal name · {c.name}</div>
                                        )}
                                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#5E636E]">
                                            {c.contact_email && (
                                                <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {c.contact_email}</span>
                                            )}
                                            {c.contact_phone && (
                                                <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {c.contact_phone}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        {c.group_id && groupsById[c.group_id] && (
                                            <span
                                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-white"
                                                style={{ background: groupsById[c.group_id].color }}
                                                title={`Value tier · ${groupsById[c.group_id].name}`}
                                                data-testid={`client-group-${c.id}`}
                                            >
                                                {groupsById[c.group_id].name}
                                            </span>
                                        )}
                                        {installerIds.has(c.id) ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-medium" title="At least one user of this client has installed the app to their home screen" data-testid={`client-homescreen-${c.id}`}>
                                                <Smartphone className="h-2.5 w-2.5" /> Homescreen
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 text-zinc-500 px-2 py-0.5 text-[10px]" title="No user has added this app to their home screen yet — send a reminder" data-testid={`client-homescreen-${c.id}`}>
                                                <Globe className="h-2.5 w-2.5" /> Browser
                                            </span>
                                        )}
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                            {c.active ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                </div>
                                {c.external_source === "practice_manager" && (
                                    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                                        <PracticeManagerLink
                                            url={tenant?.practice_manager_url}
                                            label={tenant?.practice_manager_label}
                                            entityId={c.external_id}
                                            entityKind="client"
                                            testId={`client-pm-link-${c.id}`}
                                        />
                                    </div>
                                )}
                                {c.notes && <div className="mt-3 text-xs text-[#5E636E] line-clamp-2">{c.notes}</div>}
                                <div className="mt-3 pt-3 border-t border-[#E4E4E7]" onClick={(e) => e.stopPropagation()}>
                                    <EngagementFormButton client={c} onSent={load} />
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Bulk-archive confirmation */}
            <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
                <DialogContent className="max-w-md" data-testid="clients-bulk-archive-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-800">
                            <Archive className="h-4 w-4" /> Archive {selected.size} {selected.size === 1 ? "client" : "clients"}?
                        </DialogTitle>
                        <DialogDescription>
                            Use this for entities you no longer act for. They&apos;re hidden from active
                            lists but every historical record is preserved for IRD retention.
                        </DialogDescription>
                    </DialogHeader>
                    <ul className="text-sm text-[#3F444C] space-y-1.5 pl-4 list-disc">
                        <li>Removed from the Clients list and work-queue</li>
                        <li>Portal login is disabled for these entities</li>
                        <li>All documents, returns, requests &amp; timeline are kept intact</li>
                        <li>Xero / Prosaic sync stops for these entities</li>
                        <li>Reversible from the Archived tab anytime</li>
                    </ul>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={archiving}>Cancel</Button>
                        <Button
                            onClick={confirmBulkArchive}
                            disabled={archiving}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="clients-bulk-archive-confirm"
                        >
                            {archiving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Archive className="h-4 w-4" /> Archive {selected.size}</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ManageGroupsDialog
                open={manageOpen}
                onClose={() => setManageOpen(false)}
                groups={groups}
                onChanged={async () => { await Promise.all([loadGroups(), load()]); }}
            />
        </div>
    );
}

function ManageGroupsDialog({ open, onClose, groups, onChanged }) {
    const [newName, setNewName] = useState("");
    const [newColor, setNewColor] = useState("#0EA5E9");
    const [busy, setBusy] = useState(false);
    // Local, drag-reorderable copy of `groups`. Rebased on open / when
    // upstream groups change so we always start from the canonical order.
    const [order, setOrder] = useState(groups);
    const [dragId, setDragId] = useState(null);
    const [overId, setOverId] = useState(null);
    useEffect(() => { setOrder(groups); }, [groups]);

    async function addGroup() {
        if (!newName.trim()) return;
        setBusy(true);
        try {
            await api.post("/client-groups", { name: newName.trim(), color: newColor });
            setNewName("");
            await onChanged();
            toast.success("Group added");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function rename(g) {
        const next = window.prompt(`Rename group "${g.name}" to:`, g.name);
        if (!next || next === g.name) return;
        try {
            await api.patch(`/client-groups/${g.id}`, { name: next });
            await onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function removeGroup(g) {
        const conf = window.confirm(`Delete group "${g.name}"? Clients in this group will be moved to "Ungrouped".`);
        if (!conf) return;
        try {
            const { data } = await api.delete(`/client-groups/${g.id}`);
            await onChanged();
            toast.success(`Deleted "${g.name}"${data.unassigned_clients ? ` · ${data.unassigned_clients} clients unassigned` : ""}`);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function recolor(g, color) {
        try {
            await api.patch(`/client-groups/${g.id}`, { color });
            await onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function persistOrder(nextOrder) {
        // Optimistic: caller has already set local state; just push to server.
        try {
            await api.post("/client-groups/reorder", { group_ids: nextOrder.map((g) => g.id) });
            await onChanged();
        } catch (e) {
            toast.error(formatApiError(e));
            setOrder(groups); // revert to server truth
        }
    }

    function handleDragStart(id) {
        setDragId(id);
    }
    function handleDragOver(e, id) {
        e.preventDefault();
        if (id !== overId) setOverId(id);
    }
    function handleDrop(e, targetId) {
        e.preventDefault();
        if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
        const fromIdx = order.findIndex((g) => g.id === dragId);
        const toIdx = order.findIndex((g) => g.id === targetId);
        if (fromIdx < 0 || toIdx < 0) return;
        const next = order.slice();
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        setOrder(next);
        setDragId(null);
        setOverId(null);
        persistOrder(next);
    }
    function move(id, dir) {
        // Keyboard/button fallback for touch devices where HTML5 drag is
        // finicky. dir = -1 (up) | +1 (down).
        const idx = order.findIndex((g) => g.id === id);
        const target = idx + dir;
        if (idx < 0 || target < 0 || target >= order.length) return;
        const next = order.slice();
        [next[idx], next[target]] = [next[target], next[idx]];
        setOrder(next);
        persistOrder(next);
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !busy && onClose(v)}>
            <DialogContent data-testid="manage-groups-dialog">
                <DialogHeader>
                    <DialogTitle>Manage client groups</DialogTitle>
                    <DialogDescription>Drag the handle to rank tiers (top = highest priority). Rename or delete the defaults, or add your own on top.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="group-list">
                    {order.map((g, idx) => (
                        <div
                            key={g.id}
                            draggable
                            onDragStart={() => handleDragStart(g.id)}
                            onDragOver={(e) => handleDragOver(e, g.id)}
                            onDrop={(e) => handleDrop(e, g.id)}
                            onDragEnd={() => { setDragId(null); setOverId(null); }}
                            className={`flex items-center gap-2 rounded-md border p-2 transition ${
                                dragId === g.id ? "opacity-40 border-[#0F1115]" :
                                overId === g.id && dragId ? "border-[#0F1115] bg-[#F5F5F0]" : "border-[#EEEDE7]"
                            }`}
                            data-testid={`group-row-${g.id}`}
                        >
                            <span
                                className="cursor-grab active:cursor-grabbing text-[#9CA0A8] hover:text-[#0F1115] select-none"
                                title="Drag to reorder"
                                data-testid={`group-drag-${g.id}`}
                            >
                                ⋮⋮
                            </span>
                            <input type="color" value={g.color} onChange={(e) => recolor(g, e.target.value)} className="h-7 w-7 rounded border border-[#E4E4E7] cursor-pointer" data-testid={`group-color-${g.id}`} />
                            <span className="text-[13px] font-medium text-[#0F1115]">{g.name}</span>
                            <span className="text-[11.5px] text-[#5E636E]">{g.client_count} client{g.client_count === 1 ? "" : "s"}</span>
                            <div className="ml-auto flex items-center gap-1">
                                <button type="button" onClick={() => move(g.id, -1)} disabled={idx === 0} className="h-7 w-7 rounded-md border border-[#E4E4E7] text-[13px] text-[#5E636E] hover:bg-[#F5F5F0] disabled:opacity-30 disabled:cursor-not-allowed" title="Move up" data-testid={`group-up-${g.id}`}>↑</button>
                                <button type="button" onClick={() => move(g.id, 1)} disabled={idx === order.length - 1} className="h-7 w-7 rounded-md border border-[#E4E4E7] text-[13px] text-[#5E636E] hover:bg-[#F5F5F0] disabled:opacity-30 disabled:cursor-not-allowed" title="Move down" data-testid={`group-down-${g.id}`}>↓</button>
                                <Button size="sm" variant="outline" className="h-7 text-[11.5px]" onClick={() => rename(g)} data-testid={`group-rename-${g.id}`}>Rename</Button>
                                <Button size="sm" variant="outline" className="h-7 text-[11.5px] text-red-700 border-red-200 hover:bg-red-50" onClick={() => removeGroup(g)} data-testid={`group-delete-${g.id}`}>Delete</Button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-2 rounded-md border border-dashed border-[#E4E4E7] p-3">
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E] mb-2">Add a new tier</div>
                    <div className="flex items-center gap-2">
                        <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="h-9 w-9 rounded border border-[#E4E4E7] cursor-pointer" data-testid="group-new-color" />
                        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Platinum" className="flex-1" data-testid="group-new-name" />
                        <Button onClick={addGroup} disabled={busy || !newName.trim()} data-testid="group-new-add">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                        </Button>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="manage-groups-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EngagementFormButton({ client, onSent }) {
    const [open, setOpen] = useState(false);
    const [url, setUrl] = useState(client.engagement_form_url || "");
    const [busy, setBusy] = useState(false);
    const sent = !!client.engagement_form_sent_at;
    const signed = !!client.engagement_form_signed_at;

    async function submit() {
        if (!url.trim()) { toast.error("Paste the Jotform Sign URL"); return; }
        setBusy(true);
        try {
            await api.post(`/clients/${client.id}/engagement-form`, { sign_url: url.trim() });
            toast.success("Engagement form sent — link saved for the client portal");
            setOpen(false);
            onSent?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className={`inline-flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                        signed ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100" :
                        sent ? "bg-amber-50 text-amber-800 hover:bg-amber-100" :
                        "bg-zinc-50 text-[#5E636E] hover:bg-zinc-100"
                    }`}
                    data-testid={`client-engagement-btn-${client.id}`}
                >
                    <span className="inline-flex items-center gap-1.5">
                        <Mail className="h-3 w-3" />
                        {signed ? "Engagement signed" : sent ? "Engagement form sent" : "Send engagement form"}
                    </span>
                    <span className="text-[10px] opacity-70">{sent ? "edit →" : "+"}</span>
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="engagement-dialog">
                <DialogHeader>
                    <DialogTitle>Engagement form · {client.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <p className="text-[#5E636E]">
                        In Jotform, open your engagement document → <strong>Share</strong> → <strong>Share with Link</strong> → generate an invite for this client → paste the URL below.
                    </p>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Jotform Sign URL</Label>
                        <Input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://eu.jotform.com/sign/.../invite/..."
                            className="mt-1.5 font-mono text-xs"
                            data-testid="engagement-url-input"
                        />
                    </div>
                    {client.engagement_form_sent_at && (
                        <div className="text-[11px] text-[#5E636E]">
                            Last sent {new Date(client.engagement_form_sent_at).toLocaleDateString()}.
                            {signed && ` Signed ${new Date(client.engagement_form_signed_at).toLocaleDateString()}.`}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="engagement-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & notify client"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function NewClientDialog({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        nickname: "",
        contact_email: "",
        contact_phone: "",
        notes: "",
        entity_type: "",
        gst_frequency: "",
        data_source: "",
    });
    const [invite, setInvite] = useState({ make_user: true, name: "", email: "", password: "Welcome123!" });
    const [busy, setBusy] = useState(false);

    function s(k, v) { setForm((f) => ({ ...f, [k]: v })); }
    function si(k, v) { setInvite((i) => ({ ...i, [k]: v })); }

    async function submit() {
        if (!form.name) return;
        setBusy(true);
        try {
            // Strip empty optional fields so backend Optional validators pass cleanly
            const body = Object.fromEntries(
                Object.entries(form).filter(([, v]) => v !== "" && v != null)
            );
            const { data: client } = await api.post("/clients/", body);
            // optional invite
            if (invite.make_user && invite.email && invite.password) {
                try {
                    await api.post("/auth/invite", {
                        email: invite.email,
                        name: invite.name || form.nickname || form.name,
                        role: "client",
                        password: invite.password,
                        client_id: client.id,
                    });
                } catch (e) {
                    toast.warning(`Client created but invite failed: ${formatApiError(e)}`);
                }
            }
            toast.success(`Client created — ${client.name}`);
            setOpen(false);
            setForm({ name: "", nickname: "", contact_email: "", contact_phone: "", notes: "", entity_type: "", gst_frequency: "", data_source: "" });
            setInvite({ make_user: true, name: "", email: "", password: "Welcome123!" });
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="new-client-btn">
                    <Plus className="h-4 w-4" /> Add client
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="new-client-dialog">
                <DialogHeader><DialogTitle>Add a client</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Client / business name</Label>
                            <Input className="mt-1" value={form.name} onChange={(e) => s("name", e.target.value)} data-testid="nc-name" />
                        </div>
                        <div>
                            <Label>Contact (nickname)</Label>
                            <Input className="mt-1" placeholder="e.g. John" value={form.nickname} onChange={(e) => s("nickname", e.target.value)} data-testid="nc-nickname" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Contact email</Label>
                            <Input className="mt-1" type="email" value={form.contact_email} onChange={(e) => s("contact_email", e.target.value)} data-testid="nc-email" />
                        </div>
                        <div>
                            <Label>Phone</Label>
                            <Input className="mt-1" value={form.contact_phone} onChange={(e) => s("contact_phone", e.target.value)} data-testid="nc-phone" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <Label>Entity type</Label>
                            <Select value={form.entity_type} onValueChange={(v) => s("entity_type", v)}>
                                <SelectTrigger className="mt-1" data-testid="nc-entity-type">
                                    <SelectValue placeholder="Select…" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="company">Company</SelectItem>
                                    <SelectItem value="sole_trader">Sole trader</SelectItem>
                                    <SelectItem value="trust">Trust</SelectItem>
                                    <SelectItem value="partnership">Partnership</SelectItem>
                                    <SelectItem value="individual">Individual</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>GST frequency</Label>
                            <Select value={form.gst_frequency} onValueChange={(v) => s("gst_frequency", v)}>
                                <SelectTrigger className="mt-1" data-testid="nc-gst-frequency">
                                    <SelectValue placeholder="Not registered" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="monthly">Monthly</SelectItem>
                                    <SelectItem value="two_monthly">Two-monthly</SelectItem>
                                    <SelectItem value="six_monthly">Six-monthly</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Data source</Label>
                            <Select value={form.data_source} onValueChange={(v) => s("data_source", v)}>
                                <SelectTrigger className="mt-1" data-testid="nc-data-source">
                                    <SelectValue placeholder="Select…" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="xero">Xero</SelectItem>
                                    <SelectItem value="prosaic">Prosaic</SelectItem>
                                    <SelectItem value="excel">Excel</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label>Notes (internal)</Label>
                        <Textarea className="mt-1" rows={2} value={form.notes} onChange={(e) => s("notes", e.target.value)} data-testid="nc-notes" />
                    </div>
                    <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3">
                        <div className="flex items-center gap-2">
                            <UserPlus className="h-4 w-4" />
                            <span className="text-sm font-medium">Invite a portal user</span>
                        </div>
                        <p className="text-xs text-[#5E636E] mt-1">Optional — creates a login that maps to this client.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                            <Input placeholder="Contact name" value={invite.name} onChange={(e) => si("name", e.target.value)} data-testid="nc-invite-name" />
                            <Input placeholder="Login email" value={invite.email} onChange={(e) => si("email", e.target.value)} data-testid="nc-invite-email" />
                            <Input placeholder="Temp password" value={invite.password} onChange={(e) => si("password", e.target.value)} data-testid="nc-invite-pw" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="nc-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="nc-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create client"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
